// Types mirroring `apple_delegated_onboarding` — the ONE CMS-owned document per DearDoc location
// that drives the delegated Apple Business Connect onboarding chain. Authoritative source:
// `apple-integration-backend/functions/src/schemas/apple-delegated-onboarding.schema.ts`. When this
// file and that one disagree, THAT ONE WINS; this is a hand-maintained projection of it.
//
// ⚠️ THE FIELD-OWNERSHIP BOUNDARY IS THE WHOLE CONTRACT. Two writers share every step map — this
// CMS (a browser client, via the Firestore SDK) and the backend drain (via the Admin SDK). The three
// lists below are copied verbatim from the schema, where a behavioural test asserts that every step
// map's key set is EXACTLY their union. Nothing in this CMS may write a backend-owned field.
//
// ⚠️ AND THE BACKEND DOES NOT TRUST US. The Firestore rule grants write to any authenticated
// principal (design §3.1 records that role gating here is a UI convention, not an enforced control),
// so the drain re-parses everything it reads and re-checks every gate this UI claims to apply. That
// is a documented, accepted cost — not something the CMS can fix.

/* ------------------------------------------------------------------------------------------------
 * Paths
 * ---------------------------------------------------------------------------------------------- */

/** Firestore collection holding the onboarding documents. Also the FireCMS collection id and path. */
export const APPLE_DELEGATED_ONBOARDING_PATH = "apple_delegated_onboarding";

/**
 * The FIRST subcollection — showcases genuinely recur per location (staff add showcase #2 next
 * quarter), while brand / media / location / locationCta happen at most once and are embedded maps.
 *
 * ⚠️ "THE ONE SUBCOLLECTION" IS WITHDRAWN AS OF THE GALLERY WAVE. This docblock used to say
 * `showcase_requests` was the only one; `gallery_photos` below is the second, on the same reasoning
 * (N per location, so one document per row rather than an array on a step map).
 */
export const ONBOARDING_SHOWCASE_REQUESTS_PATH = "showcase_requests";

/**
 * The SECOND subcollection — one document per gallery photo on the practice's Apple place card.
 *
 * ⚠️ TRANSCRIBED FROM THE BACKEND'S `ONBOARDING_GALLERY_PHOTOS_SUBCOLLECTION`
 * (`schemas/apple-delegated-onboarding.schema.ts`), NOT IMPORTED — the two repos share no code,
 * exactly as the showcase path above is transcribed. If it drifts, the documents this console writes
 * land in a path the drain's collectionGroup query never sees, and nothing fails loudly. Change it
 * only together with that constant.
 *
 * It lives here rather than in `apple_delegated_onboarding_gallery.ts` (where it was first declared)
 * because `parseOnboardingParentRef` in `apple_delegated_onboarding.utils.ts` has to recognise it, and
 * a utils → collection import would be a cycle. The gallery collection imports it from here.
 */
export const ONBOARDING_GALLERY_PHOTOS_PATH = "gallery_photos";

/**
 * The deterministic document id, from DearDoc's OWN ids (schema `delegatedOnboardingDocId`).
 *
 * Apple's ids appear nowhere in the key on purpose: staff never type a `companyId` (the backend
 * resolves it from the grant) and Apple mints the rest. A mistyped `accountId` therefore yields
 * "no active Apple grant" rather than a write into the wrong company — the failure direction the
 * design wants (§3.6).
 */
export const delegatedOnboardingDocId = (accountId: string, locationId: string): string =>
  `${accountId}__${locationId}`;

/* ------------------------------------------------------------------------------------------------
 * Ownership — the three lists, copied from the schema
 * ---------------------------------------------------------------------------------------------- */

/** Fields the CMS owns on a step map (`ONBOARDING_STEP_CMS_OWNED_FIELDS`). */
export const ONBOARDING_STEP_CMS_OWNED_FIELDS = [
  "input",
  "companyId",
  "queuedAt",
  "writeError",
] as const;

/**
 * Fields ONLY the backend may write on a step map (`ONBOARDING_STEP_BACKEND_OWNED_FIELDS`).
 *
 * ⚠️ Every one of these is `readOnly: true` in the FireCMS properties AND stripped from the form's
 * values by `stripFieldsTheFormMayNotWrite` in `onPreSave`. Read-only alone stops a human EDITING
 * them; the strip is what stops the form's save re-writing the values it happened to load, which is
 * how a stale `attempts` or a stale `apple` bucket gets echoed back over a fresher backend write.
 */
export const ONBOARDING_STEP_BACKEND_OWNED_FIELDS = [
  "claimedAt",
  "attempts",
  "completedAt",
  "failureCode",
  "failureMessage",
  "apple",
] as const;

/** The one shared field (`ONBOARDING_STEP_SHARED_FIELDS`). */
export const ONBOARDING_STEP_SHARED_FIELDS = ["submitState"] as const;

/**
 * The only step fields the DETAIL FORM may write — and it is deliberately narrower than "everything
 * the CMS owns".
 *
 * ⚠️ A FIRECMS FORM SUBMITS THE WHOLE DOCUMENT IT LOADED, not just what a human touched:
 * `getInitialEntityValues` seeds the form with `entity.values` verbatim, INCLUDING keys that are not
 * declared as properties at all. So without a strip, pressing Save would re-write `attempts`, an
 * `apple` bucket and `submitState` with whatever they read when the form opened — and if the drain
 * claimed the step in the meantime, that is a straight clobber of a fresher backend write.
 *
 * `submitState`, `queuedAt` and `writeError` are CMS-writable but are NOT form fields: they are
 * written only by the queue / re-open buttons, through the single helper in
 * `apple_delegated_onboarding.write.ts`. Keeping them off this list is what makes "the only way to
 * queue a step is a gated button" true rather than aspirational.
 *
 * Used as a WHITELIST by `stripFieldsTheFormMayNotWrite`, so a field added to the backend schema
 * later is excluded by default rather than silently becoming form-writable.
 */
export const ONBOARDING_STEP_FORM_OWNED_FIELDS = ["input", "companyId"] as const;

/**
 * The step fields the form must never write, as a denylist — needed at the DOCUMENT ROOT, where BOTH
 * subcollections (showcase requests and gallery photos) keep their status fields and where a
 * whitelist would strip the parent document's own identity fields.
 */
export const ONBOARDING_STEP_NON_FORM_FIELDS = [
  ...ONBOARDING_STEP_BACKEND_OWNED_FIELDS,
  ...ONBOARDING_STEP_SHARED_FIELDS,
  "queuedAt",
  "writeError",
] as const;

/**
 * The embedded steps, in the order staff work them (`ONBOARDING_STEP_NAMES`).
 *
 * `brandAssets` sits straight after `brand` because that is the order it is worked: the cover photo
 * and logo hang off the brand, and like the brand they are an Apple-side wait of days.
 */
export const ONBOARDING_STEP_NAMES = [
  "brand",
  "brandAssets",
  "media",
  "location",
  "locationCta",
  // ⚠️ LAST, AND IT IS THE ONLY STEP THAT IS NOT WORKED ONCE. The five above are a chain a practice
  // passes through during onboarding; this one is ongoing maintenance — holiday hours every December,
  // payment methods whenever the practice changes what it takes at the desk. Putting it last reads as
  // "and then, from time to time, this", which is what it is.
  "locationDetails",
] as const;
export type OnboardingStepName = (typeof ONBOARDING_STEP_NAMES)[number];

/**
 * The steps whose LIVE Apple state the drain mirrors back onto this document. Authority:
 * `ONBOARDING_MIRRORED_STEP_NAMES` in `apple-delegated-onboarding.schema.ts:765` — THREE steps as of
 * the backend's `feat/abc-drain-location-step`, which added `location` and gave its bucket a
 * `mirroredAt`.
 *
 * All three have a real outcome that arrives LATER, on a different collection, written by a different
 * scheduler: the brand feedback poller, `advanceDelegatedMedia`, and `pollDelegatedLocationFeedback`.
 * None of them touches this document, so without the mirror "is the brand PUBLISHED?", "is the image
 * APPROVED?" and "is the LOCATION published?" are not answerable from the one document the CMS reads.
 *
 * 🛑 `locationCta` is deliberately NOT mirrored, and never will be: its bucket is written by its own
 * step from a SYNCHRONOUS Apple response, so there is no external poller to mirror from.
 *
 * 🛑 NEITHER IS `locationDetails`, for the identical reason, and adding it would be actively harmful
 * rather than merely pointless: `MirrorNotice` renders a staleness caution whenever a step is on this
 * list and its bucket carries no `mirroredAt`. Nothing will ever write one for that step, so the
 * notice would be permanently true and permanently unactionable — the shape of warning staff learn to
 * scroll past, which then costs them the ones that matter.
 */
export const ONBOARDING_MIRRORED_STEP_NAMES = ["brand", "media", "location"] as const;

/** The attempts cap (`ONBOARDING_MAX_ATTEMPTS`, `drain-onboarding.runner.ts:191`). */
export const ONBOARDING_MAX_ATTEMPTS = 3;

/* ------------------------------------------------------------------------------------------------
 * Enums
 * ---------------------------------------------------------------------------------------------- */

/**
 * One step's intent lifecycle. Staff only ever cause `DRAFT → QUEUED`; everything else is written by
 * the drain.
 *
 * The `REJECTED` / `NEEDS_ATTENTION` split IS the duplicate-prevention story (design §3.7):
 * `REJECTED` means we KNOW no Apple resource was created, so it is safely re-queueable;
 * `NEEDS_ATTENTION` means the Apple outcome is UNKNOWN or the attempts cap is blown, and Apple's
 * creates take no idempotency key, so a blind retry can mint a DUPLICATE live resource.
 */
export enum OnboardingSubmitStateEnum {
  DRAFT = "DRAFT",
  QUEUED = "QUEUED",
  PROCESSING = "PROCESSING",
  SUBMITTED = "SUBMITTED",
  REJECTED = "REJECTED",
  NEEDS_ATTENTION = "NEEDS_ATTENTION",
}

/**
 * The submit states the CMS is permitted to write (`CMS_WRITABLE_SUBMIT_STATES`). Anything else on
 * that field from this app is a UI bug.
 */
export const CMS_WRITABLE_SUBMIT_STATES: readonly OnboardingSubmitStateEnum[] = [
  OnboardingSubmitStateEnum.DRAFT,
  OnboardingSubmitStateEnum.QUEUED,
];

/**
 * ⚠️ NOT A CLOSED SET IN PRACTICE. The backend's `OnboardingFailureCodeEnum` keeps growing
 * (it has more values than this copy); a shipped CMS build will meet codes that are not listed here.
 *
 * This enum therefore exists for the RE-QUEUEABILITY LOOKUP and for a friendly label, never as a
 * parse gate. `failureMessage` is always rendered verbatim and an unrecognised code falls back to
 * `submitState` (see `isRequeueable`). Adding a value here is a nicety, never a requirement.
 */
export enum OnboardingFailureCodeEnum {
  INPUT_INVALID = "INPUT_INVALID",
  GRANT_NOT_FOUND = "GRANT_NOT_FOUND",
  GRANT_INACTIVE = "GRANT_INACTIVE",
  GRANT_CONFLICT = "GRANT_CONFLICT",
  GRANT_ACCOUNT_MISMATCH = "GRANT_ACCOUNT_MISMATCH",
  BRAND_LIST_UNAVAILABLE = "BRAND_LIST_UNAVAILABLE",
  APPLE_REJECTED = "APPLE_REJECTED",
  MEDIA_UNUSABLE = "MEDIA_UNUSABLE",
  MEDIA_UPLOAD_REJECTED = "MEDIA_UPLOAD_REJECTED",
  BRAND_NOT_READY = "BRAND_NOT_READY",
  MEDIA_NOT_APPROVED = "MEDIA_NOT_APPROVED",
  CTA_NOT_OFFERED = "CTA_NOT_OFFERED",
  CTA_LIST_UNAVAILABLE = "CTA_LIST_UNAVAILABLE",
  BRAND_NOT_PUBLISHED = "BRAND_NOT_PUBLISHED",
  LOCATION_NOT_READY = "LOCATION_NOT_READY",
  LOCATION_NOT_PUBLISHED = "LOCATION_NOT_PUBLISHED",
  LOCATION_DETAILS_INCOMPLETE = "LOCATION_DETAILS_INCOMPLETE",
  CTA_UPDATE_OUTCOME_UNKNOWN = "CTA_UPDATE_OUTCOME_UNKNOWN",
  // The brand-asset step's codes. Five of the six are re-queueable BECAUSE THE STEP READS BEFORE IT
  // WRITES: each provably created nothing, and a re-queue ADOPTS what Apple already holds rather than
  // duplicating it. The exception is called out just below.
  BRAND_ASSET_IMAGE_UNUSABLE = "BRAND_ASSET_IMAGE_UNUSABLE",
  BRAND_ASSET_UPLOAD_REJECTED = "BRAND_ASSET_UPLOAD_REJECTED",
  BRAND_ASSET_REJECTED = "BRAND_ASSET_REJECTED",
  BRAND_ASSET_PAIR_INCOMPLETE = "BRAND_ASSET_PAIR_INCOMPLETE",
  BRAND_ASSET_PRECHECK_UNAVAILABLE = "BRAND_ASSET_PRECHECK_UNAVAILABLE",
  // ⚠️ NOT re-queueable — see NON_REQUEUEABLE_FAILURE_CODES below.
  BRAND_ASSET_STATE_AMBIGUOUS = "BRAND_ASSET_STATE_AMBIGUOUS",
  // The gallery-photo step's codes, from the backend's `REQUEUEABLE_FAILURE_CODES` block
  // (`apple-delegated-onboarding.schema.ts`). Six are re-queueable, and NOT for the reason the
  // brand-asset five are: Apple permits a hundred GALLERY assets per location, so "Apple allows only
  // one per intent" is unavailable here. What makes a re-queue safe is the STEM MATCH — the backend
  // asks Apple which assets already carry a version of this photo's deterministic `partnersAssetId`
  // before writing, so a retry after an ambiguous create ADOPTS rather than duplicates.
  GALLERY_PHOTO_IMAGE_UNUSABLE = "GALLERY_PHOTO_IMAGE_UNUSABLE",
  GALLERY_PHOTO_UPLOAD_REJECTED = "GALLERY_PHOTO_UPLOAD_REJECTED",
  GALLERY_PHOTO_PRECHECK_UNAVAILABLE = "GALLERY_PHOTO_PRECHECK_UNAVAILABLE",
  GALLERY_PHOTO_REJECTED = "GALLERY_PHOTO_REJECTED",
  GALLERY_ASSET_LIST_UNAVAILABLE = "GALLERY_ASSET_LIST_UNAVAILABLE",
  // ⚠️ RE-QUEUEABLE BUT IT WILL REFUSE AGAIN until a photo is removed from Apple. That is correct
  // rather than a trap: nothing was created, so the safety test is passed, and the backend's message
  // names the count, the cap and the remedy. Parking it for a human would imply a duplicate risk that
  // does not exist.
  GALLERY_FULL = "GALLERY_FULL",
  // ⚠️ NOT re-queueable — see NON_REQUEUEABLE_FAILURE_CODES below.
  GALLERY_ASSET_UNRESOLVABLE = "GALLERY_ASSET_UNRESOLVABLE",
  SHOWCASE_CREATIVE_ORPHANED = "SHOWCASE_CREATIVE_ORPHANED",
  OUTCOME_UNKNOWN = "OUTCOME_UNKNOWN",
  ATTEMPTS_EXHAUSTED = "ATTEMPTS_EXHAUSTED",
  // The practice picked an Apple location it already has in DashOne, so DearDoc writes nothing to
  // it; and the fail-closed twin when that pick can't be read. Both refuse before any Apple call, so
  // both are re-queueable (after the pick changes to "Not listed", or once Firestore answers).
  LINKED_TO_EXISTING_LOCATION = "LINKED_TO_EXISTING_LOCATION",
  LINK_CHECK_UNAVAILABLE = "LINK_CHECK_UNAVAILABLE",
}

/**
 * The codes that need a human to check Apple before anyone re-queues — the ONLY part of the failure
 * taxonomy this console hand-maintains.
 *
 * ⚠️ "THE THREE CODES" IS WITHDRAWN. This docblock said three while the list already held four
 * (`BRAND_ASSET_STATE_AMBIGUOUS` joined it and the sentence was not updated), and the gallery wave
 * makes it five. The count is deliberately no longer stated: it is the list below, and a number in
 * prose is one more thing that can be wrong.
 *
 * Authority: `REQUEUEABLE_FAILURE_CODES` in
 * `apple-integration-backend/functions/src/schemas/apple-delegated-onboarding.schema.ts`, which is
 * exactly the enum MINUS these, a property the backend asserts in its own schema test.
 *
 * ⚠️ THE MEMBERSHIP TEST IS "WE KNOW SOMETHING MAY EXIST AT APPLE", not "the call never left our
 * process". An Apple 400 is re-queueable (Apple read the request and refused it); a socket timeout on
 * the POST is not; and `SHOWCASE_CREATIVE_ORPHANED` is the case where we KNOW a creative was created.
 *
 * ⚠️ `CTA_UPDATE_OUTCOME_UNKNOWN` IS DELIBERATELY ABSENT even though its outcome is ambiguous. That
 * step's Apple write is an idempotent GET → merge → full-replace PUT, so re-running it converges
 * rather than duplicating. Do not "simplify" it together with `OUTCOME_UNKNOWN`, which is shared with
 * the four non-idempotent CREATES.
 */
export const NON_REQUEUEABLE_FAILURE_CODES: readonly string[] = [
  OnboardingFailureCodeEnum.SHOWCASE_CREATIVE_ORPHANED,
  OnboardingFailureCodeEnum.OUTCOME_UNKNOWN,
  OnboardingFailureCodeEnum.ATTEMPTS_EXHAUSTED,
  // ⚠️ THE ODD ONE OUT, and deliberately here. Nothing was created OR deleted when the backend writes
  // this — so it is NOT the ambiguous-outcome hazard the three above are. It is here because a
  // re-queue cannot CLEAR it either: what the backend refuses to act on is APPLE's own reported state
  // (two live assets where Apple documents one, or a rejected asset with no etag to delete it by).
  // Resetting the attempts cap would let staff re-queue forever against an unchanged Apple, so keeping
  // it off the re-queueable list is what parks it for engineering to look at.
  OnboardingFailureCodeEnum.BRAND_ASSET_STATE_AMBIGUOUS,
  // ⚠️ THE GALLERY TWIN OF THE CODE ABOVE, and here for the identical reason. Nothing was created
  // when the backend writes it, so this is not the ambiguous-outcome hazard — but a re-queue cannot
  // clear it either, because what the backend refuses to act on is APPLE's own reported state (an
  // asset whose intent or state cannot be read). Resetting the attempts cap would let staff re-queue
  // forever against an unchanged Apple; a human has to look at the location's assets.
  OnboardingFailureCodeEnum.GALLERY_ASSET_UNRESOLVABLE,
];

/**
 * Failure codes that PROVE no Apple resource was created, so staff may re-queue directly.
 *
 * ⚠️ DERIVED, NOT COPIED, AND THAT IS THE POINT. This list was hand-synced twice and drifted twice
 * against a backend that keeps adding codes — most recently `GRANT_ACCOUNT_MISMATCH`, whose absence
 * made this console warn that a duplicate brand might exist for a code the backend raises BEFORE it
 * decrypts a token. Deriving it as "the enum minus `NON_REQUEUEABLE_FAILURE_CODES`" moves the
 * hand-maintained surface from eighteen values to three, and inverts which way a missed sync fails.
 *
 * ⚠️ WHY DEFAULTING AN UNKNOWN CODE TO RE-QUEUEABLE IS THE SAFE DEFAULT — the argument matters more
 * than the mechanism. The backend's own invariant is `needsAttention === !requeueable`, so a code
 * that genuinely is not safe to re-queue ALWAYS arrives with `submitState: 'NEEDS_ATTENTION'`, and
 * `evaluateCommonGate` blocks that state outright before any code is consulted. A code this build has
 * never heard of therefore produces a conservative block on the dangerous path and a quiet allow on
 * the harmless one — never the reverse, and never a fabricated duplicate-hazard warning.
 */
export const REQUEUEABLE_FAILURE_CODES: readonly string[] = Object.values(
  OnboardingFailureCodeEnum,
).filter((code) => !NON_REQUEUEABLE_FAILURE_CODES.includes(code));

/** Apple's raw resource state (`AppleResourceStateEnum`). `PUBLISHED` is the §2.7 / Get-CTAs gate. */
export enum AppleResourceStateEnum {
  APPROVED = "APPROVED",
  DELETED = "DELETED",
  FAILED = "FAILED",
  IN_REVIEW = "IN_REVIEW",
  PROCESSING = "PROCESSING",
  PUBLISH_PAUSED = "PUBLISH_PAUSED",
  PUBLISHED = "PUBLISHED",
  REJECTED = "REJECTED",
  SUBMITTED = "SUBMITTED",
}

/** Apple's raw media state (`AppleMediaStateEnum`). `APPROVED` is the showcase gate. */
export enum AppleMediaStateEnum {
  APPROVED = "APPROVED",
  DELETED = "DELETED",
  IN_REVIEW = "IN_REVIEW",
  REJECTED = "REJECTED",
  SUBMITTED = "SUBMITTED",
}

/** DearDoc contract vocabulary for a delegated brand. */
export enum DelegatedBrandContractStateEnum {
  APPROVED = "APPROVED",
  IN_REVIEW = "IN_REVIEW",
  REJECTED = "REJECTED",
  FAILED = "FAILED",
  OTHER = "OTHER",
}

/** Did we CREATE this brand, or adopt one that already existed? */
export enum DelegatedBrandOriginEnum {
  CREATED = "CREATED",
  RESOLVED_EXISTING = "RESOLVED_EXISTING",
}

/** DearDoc contract vocabulary for a delegated image. */
export enum DelegatedMediaContractStateEnum {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  OTHER = "OTHER",
}

/**
 * DearDoc contract vocabulary for a delegated location.
 *
 * Authority: `DelegatedLocationContractStateEnum` in
 * `apple-integration-backend/functions/src/schemas/apple-delegated-location.schema.ts:26` — SEVEN
 * values. An earlier version of this mirror listed only the first four, which would have rendered a
 * published or failed location as a raw enum string.
 */
export enum DelegatedLocationContractStateEnum {
  SUBMISSION_FAILED = "SUBMISSION_FAILED",
  REJECTED = "REJECTED",
  ACTION_REQUIRED = "ACTION_REQUIRED",
  CREATED = "CREATED",
  PUBLISHED = "PUBLISHED",
  FAILED = "FAILED",
  OTHER = "OTHER",
}

/** DearDoc contract vocabulary for a showcase request. `PARTIALLY_CREATED` is the orphan hazard. */
export enum DelegatedShowcaseRequestContractStateEnum {
  SUBMISSION_FAILED = "SUBMISSION_FAILED",
  REJECTED = "REJECTED",
  PARTIALLY_CREATED = "PARTIALLY_CREATED",
  SUBMITTED = "SUBMITTED",
}

/**
 * The Apple environments results are bucketed under (`apple.<ENV>`).
 *
 * ⚠️ THE DEPLOYED BACKEND WRITES `DQE`. `APPLE_ENVIRONMENT` (backend `constants.ts:54`) is derived
 * from the Business Connect host, and the deployed function leaves that at the DQE default. Nothing
 * in this CMS can observe that, which is exactly why the displayed environment is derived from the
 * document and labelled on screen rather than assumed.
 */
export enum AppleEnvironmentEnum {
  PRODUCTION = "PRODUCTION",
  AIE = "AIE",
  DQE = "DQE",
}

/**
 * Apple's showcase-creative CTA enum — a CLOSED set, unlike the place-card CTA. Authority:
 * `AppleShowcaseCreativeCTAEnum` in `apple-brand-showcase-creative.schema.ts:15`.
 */
export enum ShowcaseCreativeCtaEnum {
  SCHEDULE = "SCHEDULE",
  WEBSITE = "WEBSITE",
}

/**
 * Apple's brand ownership types.
 *
 * ⚠️ RE-VERIFY THESE AGAINST THE BACKEND RATHER THAN TRUSTING THIS COMMENT. The authority is
 * `AppleBrandOwnershipTypeEnum` in
 * `apple-integration-backend/functions/src/schemas/apple-brand.schema.ts:12`
 * (`z.enum(['BRAND_OWNER', 'FRANCHISEE'])`), consumed by `NormalizedBrandInputSchema.ownershipType`
 * in `delegated-brand-request.schema.ts:25`.
 *
 * ⚠️ AND WHY IT MATTERS MORE THAN A LABEL: Zod parses the WHOLE `brand.input` object, so ONE value
 * outside this set fails the entire brand input and the step is rejected `INPUT_INVALID`. An earlier
 * version of this enum shipped `FRANCHISE` / `INDEPENDENT` / `CHAIN` — three values with ZERO overlap
 * with the contract — so every option the dropdown offered broke the step. A doc comment asserting
 * the values were correct is what stopped that being caught; hence the file citation above.
 */
export enum BrandOwnershipTypeEnum {
  BRAND_OWNER = "BRAND_OWNER",
  FRANCHISEE = "FRANCHISEE",
}

/**
 * Apple URL types accepted on a brand's `urls`. Authority: `AppleUrlTypesEnum` in
 * `apple-action.schema.ts:106`; verified element-by-element against it.
 */
export enum BrandUrlTypeEnum {
  FACEBOOK = "FACEBOOK",
  HOMEPAGE = "HOMEPAGE",
  INSTAGRAM = "INSTAGRAM",
  IOS_APP = "IOS_APP",
  TWITTER = "TWITTER",
  YELP = "YELP",
}

/**
 * The days a location's opening hours may name.
 *
 * ⚠️ LOWERCASE, AND THAT IS NOT A TYPO. The authority is the inline enum on
 * `NormalizedLocationInputSchema.openingHours[].day` (`delegated-location-request.schema.ts:25`),
 * which is lowercase. The UPPERCASE `DayOfWeekEnum` in `apple-action.schema.ts` belongs to the
 * frozen legacy Apple-action schema and is a DIFFERENT contract — copying that one would fail the
 * whole location input parse.
 */
export enum LocationOpeningDayEnum {
  monday = "monday",
  tuesday = "tuesday",
  wednesday = "wednesday",
  thursday = "thursday",
  friday = "friday",
  saturday = "saturday",
  sunday = "sunday",
}

/**
 * A location is either open for business or closed. Authority: the inline
 * `z.enum(['OPEN', 'CLOSED'])` on `NormalizedLocationInputSchema.status`
 * (`delegated-location-request.schema.ts:33`), which is `.optional()` and NOT `.nullable()` — see
 * `pruneEmptyLeaves` for why clearing it must omit the key rather than write `null`.
 */
export enum LocationStatusEnum {
  OPEN = "OPEN",
  CLOSED = "CLOSED",
}

/* ------------------------------------------------------------------------------------------------
 * Step shapes
 * ---------------------------------------------------------------------------------------------- */

/** The status fields EVERY step carries. Ownership is annotated per field. */
export type OnboardingStepStatus = {
  /** SHARED — the CMS writes only `DRAFT` or `QUEUED`. */
  submitState?: OnboardingSubmitStateEnum;
  /** CMS-owned — when staff pressed the button. Audit only; the drain does not order by it. */
  queuedAt?: Date | null;
  /** CMS-owned — "I could not even enqueue". The backend never reads or writes it. */
  writeError?: string | null;
  /** BACKEND-owned — when the drain claimed the step. */
  claimedAt?: Date | null;
  /** BACKEND-owned — Apple ATTEMPTS, incremented inside the claim transaction. Capped at 3. */
  attempts?: number | null;
  /** BACKEND-owned — when the step reached a terminal state. */
  completedAt?: Date | null;
  /** BACKEND-owned — see `OnboardingFailureCodeEnum`. Typed loosely: the enum grows. */
  failureCode?: string | null;
  /** BACKEND-owned — the human sentence, written for staff. ALWAYS rendered verbatim. */
  failureMessage?: string | null;
};

/** Fields common to the env buckets of the two MIRRORED steps (brand, media). */
type MirroredEnvBucket = {
  /**
   * ⚠️ THE STALENESS SIGNAL. Absent ⇒ `state` / `contractState` are CREATE-TIME SEEDS, not live.
   * Present ⇒ the drain's mirror phase refreshed them from the authoritative collection at this
   * time. A `mirroredAt` far in the past means the mirror has stopped running.
   */
  mirroredAt?: Date | null;
};

/** `brand.apple.<ENV>` — `EnvScopedOnboardingBrandStateSchema`. All BACKEND-written. */
export type EnvScopedBrandState = MirroredEnvBucket & {
  brandId?: string | null;
  /** The CLIENT company the brand was created under, resolved from the grant. A LOOKUP KEY only. */
  companyId?: string | null;
  state?: AppleResourceStateEnum | null;
  contractState?: DelegatedBrandContractStateEnum | null;
  origin?: DelegatedBrandOriginEnum | null;
  partnersBrandId?: string | null;
  validationReports?: unknown[] | null;
  gaps?: unknown[] | null;
  observedAt?: Date | null;
};

/** One landed brand asset inside the pair bucket. All BACKEND-written. */
export type EnvScopedBrandAssetState = {
  assetId?: string | null;
  imageId?: string | null;
  partnersAssetId?: string | null;
  state?: string | null;
  contractState?: string | null;
  latestEtag?: string | null;
  validationReports?: unknown[] | null;
};

/**
 * `brandAssets.apple.<ENV>` — the PAIR bucket. All BACKEND-written.
 *
 * ⚠️ `pairState` IS THE ONLY FIELD THIS CONSOLE RENDERS AS A STATUS, and it is computed by the
 * backend from both assets together. Do not derive a status from `coverPhoto` / `placecardLogo` here:
 * the two-assets-to-one-word mapping is an ordered decision cascade that lives (and is tested) in the
 * backend, and a second copy of it in an untested codebase is exactly how the two come to disagree.
 */
export type EnvScopedBrandAssetsState = MirroredEnvBucket & {
  companyId?: string | null;
  brandId?: string | null;
  coverPhoto?: EnvScopedBrandAssetState | null;
  placecardLogo?: EnvScopedBrandAssetState | null;
  pairState?: string | null;
  observedAt?: Date | null;
};

/** `media.apple.<ENV>` — `EnvScopedOnboardingMediaStateSchema`. All BACKEND-written. */
export type EnvScopedMediaState = MirroredEnvBucket & {
  mediaId?: string | null;
  companyId?: string | null;
  fileName?: string | null;
  state?: AppleMediaStateEnum | null;
  contractState?: DelegatedMediaContractStateEnum | null;
  validationReports?: unknown[] | null;
  gaps?: unknown[] | null;
  observedAt?: Date | null;
};

/**
 * `location.apple.<ENV>` — `EnvScopedOnboardingLocationStateSchema`. All BACKEND-written.
 *
 * ⚠️ THIS BUCKET IS NOW MIRRORED, WHICH IT WAS NOT WHEN THIS CONSOLE WAS FIRST BUILT. It gained a
 * `mirroredAt` alongside `location` joining `ONBOARDING_MIRRORED_STEP_NAMES`, so `state` is no longer
 * a permanently-stale create-time seed and CAN now reach `PUBLISHED`. That is exactly the follow-up
 * `evaluateLocationCtaGate` was written to absorb — see the note there.
 */
export type EnvScopedLocationState = MirroredEnvBucket & {
  /** ⚠️ Apple's location id — the Apple↔DearDoc mapping AND the pin the showcase gate checks. */
  locationId?: string | null;
  companyId?: string | null;
  partnersLocationId?: string | null;
  brandId?: string | null;
  state?: AppleResourceStateEnum | null;
  contractState?: DelegatedLocationContractStateEnum | null;
  /**
   * ⚠️ True when the create went out WITHOUT the booking quicklink because no `bookingUrl` was
   * mapped. The location is valid but has NO "Schedule" button — which is the entire feature. It is
   * recorded rather than inferred precisely so a human can see it, so this surface makes it loud.
   */
  bookingOmitted?: boolean | null;
  validationReports?: unknown[] | null;
  gaps?: unknown[] | null;
  observedAt?: Date | null;
};

/** `locationCta.apple.<ENV>` — `EnvScopedOnboardingLocationCtaStateSchema`. All BACKEND-written. */
export type EnvScopedLocationCtaState = {
  /**
   * The CTA actually WRITTEN — `null` when the step wrote the place-card content but Apple did not
   * offer the requested button. Not "what staff asked for".
   */
  callToAction?: string | null;
  /**
   * ⚠️ A CAUTION, NOT A FAILURE. Set when the update wrote “About the place” / “Good to Know” but
   * could not set the booking button, because Apple does not offer it yet. For the first days after a
   * location publishes this is the ordinary state of a healthy step: Apple only offers the booking CTA
   * once it has approved the scheduling link. Re-queueing later sets the button without rewriting the
   * content.
   */
  ctaNotOfferedMessage?: string | null;
  /**
   * The “Good to Know” paths the last successful write ASSERTED. The backend diffs the next
   * selection against this to retract what staff have unticked, which is what makes removing a claim
   * from Apple Maps possible without clearing anyone else's entries.
   */
  assertedAttributes?: string[] | null;
  /** Copied from the location step's bucket, never staff-typed. */
  locationId?: string | null;
  companyId?: string | null;
  /**
   * ⚠️ What Apple's Get-CTAs read offered AT THE TIME OF THE WRITE. Populated BY the step, never
   * before it — Apple states the available values are unknowable until it knows which Apple Maps
   * location matches ours. Never build a pre-flight CTA validation on this.
   */
  offeredCtas?: unknown[] | null;
  state?: AppleResourceStateEnum | null;
  validationReports?: unknown[] | null;
  observedAt?: Date | null;
};

/** `showcase_requests/{id}.apple.<ENV>` — `EnvScopedOnboardingShowcaseStateSchema`. */
export type EnvScopedShowcaseState = {
  requestId?: string | null;
  companyId?: string | null;
  /** ⚠️ Present whenever Apple minted a creative — INCLUDING on a failed showcase create. */
  creativeId?: string | null;
  /** Present only when the showcase itself landed. */
  showcaseId?: string | null;
  contractState?: DelegatedShowcaseRequestContractStateEnum | null;
  validationReports?: unknown[] | null;
  gaps?: unknown[] | null;
  observedAt?: Date | null;
};

/**
 * `gallery_photos/{id}.apple.<ENV>` — mirrors the backend's `EnvScopedGalleryPhotoSchema`
 * (`schemas/apple-delegated-location-asset.schema.ts`).
 *
 * ⚠️ THERE IS NO `mirroredAt` HERE, AND ITS ABSENCE IS THE HONEST SIGNAL. Nothing polls location
 * assets today — the delegated feedback poller's `resourceType` filter does not cover them — so
 * `contractState` is a CREATE-TIME seed rather than live truth, and `galleryPhoto` is deliberately
 * absent from `ONBOARDING_MIRRORED_STEP_NAMES` (it could not be on that list anyway: those are
 * EMBEDDED step names). The console must therefore never present these as refreshed state.
 *
 * `state` and `contractState` are typed as open strings on purpose: the backend ingests Apple's raw
 * state permissively, so a value newer than this build must render as itself rather than blank.
 */
export type EnvScopedGalleryPhotoState = {
  companyId?: string | null;
  /** Apple's location id the asset hangs off — from the LOCATION step's bucket, never staff-typed. */
  locationId?: string | null;
  /** OUR deterministic key, and the RESOLVE key for this path. A replacement carries `-v<n>`. */
  partnersAssetId?: string | null;
  imageId?: string | null;
  assetId?: string | null;
  state?: string | null;
  contractState?: string | null;
  latestEtag?: string | null;
  deletedEtag?: string | null;
  deleteOutcome?: "DELETED" | "ALREADY_GONE" | null;
  origin?: "CREATED" | "ADOPTED" | "REPLACED" | null;
  /**
   * How many `GALLERY` assets Apple reported on this location at resolve time, and the cap it was
   * checked against. Recorded because "the gallery is full" is the one refusal on this path staff
   * cannot diagnose from their own input — they need to know what Apple thinks is already there.
   */
  galleryCount?: number | null;
  galleryCapacity?: number | null;
  validationReports?: unknown[] | null;
  gaps?: unknown[] | null;
  observedAt?: Date | null;
};

/** An env-scoped bucket keyed by environment. A document may carry any subset. */
export type AppleEnvScoped<T> = {
  PRODUCTION?: T;
  AIE?: T;
  DQE?: T;
};

/* ------------------------------------------------------------------------------------------------
 * Step inputs — the CMS-owned half
 * ---------------------------------------------------------------------------------------------- */

export type BrandUrl = { url: string; type: BrandUrlTypeEnum };

/** `NormalizedBrandInputSchema`. Only `name` is required. */
export type OnboardingBrandInput = {
  name?: string;
  /** A plain string, NOT the closed specialty enum: an unmapped value must reach the brand mapper. */
  practiceType?: string;
  categories?: string[];
  countryCode?: string;
  website?: string;
  urls?: BrandUrl[];
  ownershipType?: BrandOwnershipTypeEnum;
  locale?: string;
  partnersBrandId?: string;
};

/**
 * `OnboardingMediaInputSchema`.
 *
 * ⚠️ `imageUrl` is deliberately NOT a validated URL on the backend: it is a DearDoc storage URL, and
 * a shape mismatch must surface as an actionable step failure with a message, not as an unparseable
 * document the drain silently skips. This CMS matches that — a plain string property, no url mode.
 */
export type OnboardingMediaInput = {
  fileName?: string;
  imageUrl?: string;
};

/** One brand-asset image: the same `{ fileName, imageUrl }` shape the media step uses. */
export type BrandAssetImageInput = {
  fileName?: string;
  imageUrl?: string;
};

/**
 * `OnboardingBrandAssetsInputSchema`.
 *
 * ⚠️ BOTH IMAGES ARE REQUIRED, and that is Apple's rule, not a form convenience. Apple renders the
 * branding as a SET, so submitting one alone puts an image through an Apple review cycle of several
 * days and displays NOTHING at the end of it.
 *
 * ⚠️ `altText` IS ONE FIELD FOR BOTH ASSETS, matching the backend. Apple's guidance is that alt text
 * is concise and describes the business, which is the same sentence for a practice's cover photo and
 * its logo — two fields would be filled identically almost every time.
 *
 * ⚠️ THERE IS NO CAPTION FIELD, and one must not be added. `captions` does not exist on a brand asset,
 * even though a `BrandAssetCaptionRequestV3` shape appears in the OpenAPI document.
 */
export type OnboardingBrandAssetsInput = {
  coverPhoto?: BrandAssetImageInput;
  placecardLogo?: BrandAssetImageInput;
  altText?: string;
  locale?: string;
};

export type LocationAddress = {
  street?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  fullAddress?: string;
};

export type LocationCoordinates = { latitude?: number; longitude?: number };

export type LocationOpeningHours = {
  day?: LocationOpeningDayEnum;
  opensAt?: string;
  closesAt?: string;
};

/** One `descriptions[]` row on a special-hours entry. Both halves are required by Apple when the row
 * exists; a row with no text is dropped rather than rejecting the entry. */
export type LocationSpecialHoursDescription = {
  text?: string;
  locale?: string;
};

/**
 * One holiday / seasonal hours entry. `NormalizedSpecialHoursSchema`.
 *
 * ⚠️ `startDate` / `endDate` are STRINGS, not timestamps — the same reason the showcase dates are:
 * the backend parses them with a string schema and a FireCMS `date` property would write a Firestore
 * Timestamp that fails that parse. Format is `YYYY-MM-DD` (calendar date only, NOT the full ISO
 * instant the showcase dates use — a different backend field with a different rule).
 *
 * ⚠️ EVERY FIELD IS OPTIONAL, matching the backend schema, and that is deliberate: a half-filled row
 * reaches the mapper and comes back as a readable step failure naming the dates, rather than failing
 * the whole location-input parse the way a half-filled OPENING-hours row does.
 */
export type LocationSpecialHours = {
  startDate?: string;
  endDate?: string;
  closed?: boolean;
  hoursByDay?: LocationOpeningHours[];
  descriptions?: LocationSpecialHoursDescription[];
};

/** `NormalizedLocationInputSchema`. Only `name` is required. */
export type OnboardingLocationInput = {
  name?: string;
  practiceType?: string;
  status?: LocationStatusEnum;
  address?: LocationAddress;
  coordinates?: LocationCoordinates;
  openingHours?: LocationOpeningHours[];
  specialHours?: LocationSpecialHours[];
  /** Values from `PAYMENT_METHOD_LABELS`. An unknown one fails the step with a named reason. */
  paymentMethods?: string[];
  phone?: string;
  website?: string;
  /** ⚠️ Absent ⇒ the create goes out with NO booking quicklink and `bookingOmitted` is recorded. */
  bookingUrl?: string;
  locale?: string;
};

/**
 * `OnboardingLocationCtaInputSchema`.
 *
 * ⚠️ `callToAction` is an OPEN STRING, not an enum, and that is load-bearing: Apple's available CTA
 * values are per-location and only knowable from the Get-CTAs read. A value Apple does not offer
 * must surface as an actionable `CTA_NOT_OFFERED` against a real list, not as an unparseable
 * document. Absent ⇒ the backend uses `SCHEDULE`, the booking button this feature exists for.
 */
export type OnboardingLocationCtaInput = {
  callToAction?: string;
};

/**
 * `NormalizedShowcaseInputSchema`.
 *
 * ⚠️ `startDate` / `endDate` are STRINGS, not timestamps — JSON has no date type and the backend
 * parses them with `z.string()`. A FireCMS `date` property would write a Firestore Timestamp and
 * fail that parse, so they are string properties here on purpose.
 */
export type OnboardingShowcaseInput = {
  /** Required by the shared schema. The PARENT document's `accountId` is the authority if they differ. */
  accountId?: string;
  /** ⚠️ APPLE's location id. Pinned against `location.apple.<ENV>.locationId` server-side. */
  locationId?: string;
  /** ⚠️ APPLE's brand id. Pinned against `brand.apple.<ENV>.brandId` server-side. */
  brandId?: string;
  /** ⚠️ APPLE's image id. Must be APPROVED by Apple before a creative can be built on it. */
  mediaId?: string;
  headline?: string;
  body?: string;
  altText?: string;
  callToAction?: ShowcaseCreativeCtaEnum;
  startDate?: string;
  endDate?: string;
  locale?: string;
};

/** One caption on a gallery photo — `GalleryPhotoCaptionInputSchema`. */
export type GalleryPhotoCaptionInput = {
  /** ⚠️ REQUIRED WITHIN A ROW (`z.string().min(1)`), so a half-filled row rejects the whole photo. */
  title?: string;
  /** ⚠️ ALSO REQUIRED WITHIN A ROW. What a screen reader announces in place of the photo. */
  altText?: string;
  /** Nullish — the backend's payload builder defaults it rather than sending it blank. */
  locale?: string;
};

/**
 * `OnboardingGalleryPhotoInputSchema` — the CMS-owned `input` of ONE `gallery_photos` document.
 *
 * ⚠️ THERE IS NO `photoId` FIELD, AND ITS ABSENCE IS THE DESIGN. The photo's identity is the
 * `gallery_photos` DOCUMENT ID, which the backend derives its Apple resolve key (`partnersAssetId`)
 * from. A CMS-minted id field would be staff-editable, and re-pointing it silently re-points the
 * photo at a different Apple asset. See the collection file for the consequence staff will hit:
 * deleting a row does NOT remove the photo from Apple.
 *
 * ⚠️ `photo` IS A NESTED MAP RATHER THAN TWO FLAT FIELDS, AND THE CMS FORCES THAT. The cropper field
 * calls `setValue({ fileName, imageUrl })`, replacing the map it is bound to WHOLESALE — bound to
 * `input` it would wipe `captions` and `source` on every crop.
 */
export type OnboardingGalleryPhotoInput = {
  photo?: { fileName?: string; imageUrl?: string };
  captions?: GalleryPhotoCaptionInput[];
  source?: keyof typeof GALLERY_PHOTO_SOURCE_LABELS;
};

/* ------------------------------------------------------------------------------------------------
 * Step maps and the document
 * ---------------------------------------------------------------------------------------------- */

/**
 * One step map. `TInput` is the staff-supplied intent; `TApple` is the backend's env-scoped result.
 *
 * `companyId` is CMS-writable and must NORMALLY BE LEFT EMPTY — see the property `description` in
 * the collection, and `GRANT_CONFLICT` in the failure codes, for its one legitimate use.
 */
export type OnboardingStep<TInput, TApple> = OnboardingStepStatus & {
  input?: TInput | null;
  companyId?: string | null;
  apple?: AppleEnvScoped<TApple> | null;
};

export type OnboardingBrandStep = OnboardingStep<OnboardingBrandInput, EnvScopedBrandState>;
export type OnboardingBrandAssetsStep = OnboardingStep<
  OnboardingBrandAssetsInput,
  EnvScopedBrandAssetsState
>;
export type OnboardingMediaStep = OnboardingStep<OnboardingMediaInput, EnvScopedMediaState>;
export type OnboardingLocationStep = OnboardingStep<
  OnboardingLocationInput,
  EnvScopedLocationState
>;
export type OnboardingLocationCtaStep = OnboardingStep<
  OnboardingLocationCtaInput,
  EnvScopedLocationCtaState
>;

/** `locationDetails.apple.<ENV>` — `EnvScopedOnboardingLocationDetailsStateSchema`. All BACKEND-written. */
export type EnvScopedLocationDetailsState = {
  /** The Apple location the update addressed — copied from the location step, never staff-typed. */
  locationId?: string | null;
  companyId?: string | null;
  /**
   * The holiday entries the last successful run actually SENT — not what the form holds now.
   *
   * ⚠️ THE TWO GENUINELY DIFFER, which is why this is recorded rather than inferred: a description row
   * with no text is dropped on the way out, and someone editing the form after a run leaves this
   * showing the older, sent version. This bucket is the only after-the-fact evidence of what reached a
   * client's place card.
   */
  specialHours?: unknown[] | null;
  /** The payment-method values the last successful run SENT, after the vocabulary check. */
  paymentMethods?: string[] | null;
  state?: string | null;
  validationReports?: unknown[] | null;
  observedAt?: Date | null;
};

/**
 * The `locationDetails` step — the recurring converging update.
 *
 * ⚠️ ITS `input` IS EMPTY AND IS READ FROM NOWHERE. The step sends the holiday hours and payment
 * methods from the LOCATION step's own form (`location.input`), so there is ONE place to edit them and
 * two verbs over it: the location step creates, this step updates. A second copy of those fields would
 * silently disagree with the first the moment anyone edited the wrong one.
 *
 * ⚠️ SO IT BREAKS THE "EVERY STEP OWNS ITS INPUT" SYMMETRY EVERY OTHER STEP HERE FOLLOWS, and a reader
 * tracing this step's data will not find it under this step. Stated here, and again on the step panel
 * where staff read it. The key exists at all because the backend's field-ownership test asserts that
 * every step map's key set is exactly the declared CMS / backend / shared lists.
 */
export type OnboardingLocationDetailsStep = OnboardingStep<
  Record<string, never>,
  EnvScopedLocationDetailsState
>;

/**
 * One showcase request — a document in the `showcase_requests` SUBCOLLECTION.
 *
 * ⚠️ ITS STATUS FIELDS SIT AT THE DOCUMENT ROOT (`submitState`, not `showcase.submitState`): it is
 * one request per document, so there is nothing to nest under. Every field-path helper in this
 * surface therefore takes a step name of `null` for showcases.
 */
export type OnboardingShowcaseRequestEntity = OnboardingStep<
  OnboardingShowcaseInput,
  EnvScopedShowcaseState
>;

/**
 * ONE GALLERY PHOTO — a document in the `gallery_photos` SUBCOLLECTION.
 *
 * ⚠️ ITS STATUS FIELDS SIT AT THE DOCUMENT ROOT TOO (`submitState`, not `galleryPhoto.submitState`):
 * one photo per document, so there is nothing to nest under. Structurally identical to a showcase
 * request, and every field-path helper in this surface takes a step name of `null` for both.
 *
 * It lives here rather than in `apple_delegated_onboarding_gallery.ts`, where it was first declared
 * locally: that lane did not own this file and said so, and the type belongs beside its siblings.
 */
export type OnboardingGalleryPhotoEntity = OnboardingStep<
  OnboardingGalleryPhotoInput,
  EnvScopedGalleryPhotoState
>;

export type AppleDelegatedOnboardingEntity = {
  /** CMS-written identity. Together these two are the document id. */
  accountId: string;
  locationId: string;
  /** CMS-written — carried for search, never used to resolve a grant. */
  salesforce_id?: string | null;
  /** CMS-written — free-text staff note. Never read by the backend. */
  notes?: string | null;
  brand?: OnboardingBrandStep | null;
  brandAssets?: OnboardingBrandAssetsStep | null;
  media?: OnboardingMediaStep | null;
  location?: OnboardingLocationStep | null;
  locationCta?: OnboardingLocationCtaStep | null;
  locationDetails?: OnboardingLocationDetailsStep | null;
  /** CMS-written on create; the backend never rewrites it. */
  createdAt?: Date | null;
  /** Backend-written housekeeping. Read-only here. */
  updatedAt?: Date | null;
};

/* ------------------------------------------------------------------------------------------------
 * Labels
 * ---------------------------------------------------------------------------------------------- */

/**
 * The staff-facing name for a showcase request — the subcollection's equivalent of a step label.
 *
 * ⚠️ ONE CONSTANT BECAUSE THREE COPIES ALREADY DREW APART. This string was inlined separately in the
 * write helper ("This showcase request"), the steps view ("Showcase request") and the gate ("Showcase"),
 * so a staff member could see a panel headed one way, a refusal naming it another, and a toast a
 * third. It reads correctly both sentence-initially ("Showcase request queued.") and lowercased
 * mid-sentence ("Could not queue showcase request"), which is what the step labels beside it do.
 */
export const ONBOARDING_SHOWCASE_LABEL = "Showcase request";

/**
 * The staff-facing name for ONE gallery photo — the second subcollection's equivalent of a step
 * label, and for the same one-constant reason as the showcase label above.
 *
 * Reads correctly sentence-initially ("Gallery photo queued.") and lowercased mid-sentence ("Could
 * not queue gallery photo"), which is what every label beside it does.
 */
export const ONBOARDING_GALLERY_PHOTO_LABEL = "Gallery photo";

/** Staff-facing names for the embedded steps, used in headings and refusal copy. */
export const ONBOARDING_STEP_LABELS: Record<OnboardingStepName, string> = {
  brand: "Brand",
  brandAssets: "Cover photo & logo",
  media: "Practice image",
  location: "Location",
  locationCta: "Place-card booking button",
  // Named for what it DOES to a human, not for the Apple field it writes. "specialHours" and
  // "paymentMethods" are Apple's words; "update location details" is the thing a staff member came
  // here to do.
  locationDetails: "Update location details",
};

/**
 * What each step's Apple write IS — a NARROWER PROJECTION of the backend's
 * `ONBOARDING_STEP_RESOURCES` (`services/delegated-onboarding/drain-onboarding.types.ts`).
 *
 * ⚠️ NO LONGER A MIRROR, AND THE DIFFERENCE IS DELIBERATE RATHER THAN DRIFT. The backend replaced its
 * own `mintsResource` boolean with a three-value `reissueHazard`
 * (`DUPLICATE` / `CONVERGES` / `AMBIGUOUS_END_STATE`), because a boolean could not describe a
 * DESTRUCTIVE action: `false` asserts "re-running converges, re-queue it" and `true` asserts
 * "re-queueing can create a second one", and for a delete BOTH are false.
 *
 * This table stays a boolean because **every target in it is a create or a converging update** —
 * there is no destructive queue target in this console. Showcase lifecycle actions (deactivate /
 * extend / delete) are the one destructive surface here and they do NOT come through
 * `evaluateQueueGate` at all: they have their own confirmation dialog and their own read-only audit
 * collection (`apple_showcase_lifecycle.ts`), with their own contract-state vocabulary.
 *
 * ⚠️ SO IF A DESTRUCTIVE TARGET IS EVER ADDED TO THIS TABLE, THE BOOLEAN MUST GO FIRST. Adding one
 * with `mintsResource: false` would make this console tell staff a delete is safe to re-queue, which
 * is the exact sentence the backend removed. Widen to the backend's three shapes at that point rather
 * than picking whichever boolean reads less wrong.
 *
 * ⚠️ `mintsResource` is the half that decides what a blocked message tells staff to DO. The
 * place-card CTA step mints nothing (it is a full-replace UPDATE of a location that already
 * exists), so its warnings must never send someone hunting a duplicate that cannot exist.
 *
 * ⚠️ THIS TABLE'S KEYS ARE THE SINGLE DEFINITION OF "WHAT CAN BE QUEUED". `QueueTarget` in
 * `apple_delegated_onboarding.utils.ts` is `keyof typeof` this object — the same relationship the
 * backend's `OnboardingResourceStep` has to its own copy — so a surface cannot be gated without
 * having a resource noun here, and a noun cannot be added without the gate learning about it. The
 * noun completes "check Apple Business Connect for this client's `<noun>`s".
 */
export const ONBOARDING_STEP_RESOURCES: Record<
  OnboardingStepName | "showcase" | "galleryPhoto",
  { noun: string; mintsResource: boolean }
> = {
  brand: { noun: "brand", mintsResource: true },
  // Singular noun for a PAIR, deliberately: staff act on "the branding", and a message telling
  // someone to hunt for "brand assets" in Apple Business Connect reads as jargon.
  brandAssets: { noun: "cover photo and logo", mintsResource: true },
  // NOT "media" — Apple calls these images, and every message in this chain says "image".
  media: { noun: "image", mintsResource: true },
  location: { noun: "location", mintsResource: true },
  locationCta: { noun: "place-card booking button", mintsResource: false },
  // ⚠️ `mintsResource: false`, and here it is right for the same reason as the CTA step above: the
  // write is a full-replace UPDATE of a location that already exists. Nothing is minted, so no message
  // may send a staff member hunting a duplicate. See the backend's `reissueHazard: 'CONVERGES'` entry
  // for the longer argument — including why "creates nothing" is NOT on its own sufficient.
  locationDetails: { noun: "holiday hours and payment method", mintsResource: false },
  showcase: { noun: "showcase", mintsResource: true },
  // ⚠️ "gallery photo", matching every gallery message the backend writes
  // (`delegated-location-asset.errors.ts` says "gallery photo" throughout) — NOT "location asset",
  // which is the API's word and not a thing any staff member can find in Apple Business Connect.
  //
  // ⚠️ `mintsResource: true`, DELIBERATELY, EVEN THOUGH THE STEP READS BEFORE IT WRITES. The backend
  // resolves the photo by its deterministic `partnersAssetId` stem before creating, so a retry after
  // an ambiguous create normally ADOPTS rather than duplicates — but "the next read will tell us" is a
  // different claim from "nothing was created", and this flag is what decides whether a staff member
  // is told to go and look. The create is a non-idempotent POST that takes no idempotency key, and
  // Apple permits a hundred GALLERY assets per location, so the one-per-intent argument the
  // brand-asset step leans on is simply unavailable here. Conservative classification stays.
  galleryPhoto: { noun: "gallery photo", mintsResource: true },
};

/** Plain-language labels for the intent lifecycle, for the FireCMS enum property and the chip. */
export const SUBMIT_STATE_LABELS: Record<string, string> = {
  [OnboardingSubmitStateEnum.DRAFT]: "Draft",
  [OnboardingSubmitStateEnum.QUEUED]: "Queued",
  [OnboardingSubmitStateEnum.PROCESSING]: "Processing",
  [OnboardingSubmitStateEnum.SUBMITTED]: "Submitted to Apple",
  [OnboardingSubmitStateEnum.REJECTED]: "Rejected — safe to re-queue",
  [OnboardingSubmitStateEnum.NEEDS_ATTENTION]: "Needs attention — check Apple first",
};

export const APPLE_RESOURCE_STATE_LABELS: Record<string, string> = {
  [AppleResourceStateEnum.APPROVED]: "Approved",
  [AppleResourceStateEnum.DELETED]: "Deleted",
  [AppleResourceStateEnum.FAILED]: "Failed",
  [AppleResourceStateEnum.IN_REVIEW]: "In review",
  [AppleResourceStateEnum.PROCESSING]: "Processing",
  [AppleResourceStateEnum.PUBLISH_PAUSED]: "Publishing paused",
  [AppleResourceStateEnum.PUBLISHED]: "Published",
  [AppleResourceStateEnum.REJECTED]: "Rejected",
  [AppleResourceStateEnum.SUBMITTED]: "Submitted",
};

export const APPLE_MEDIA_STATE_LABELS: Record<string, string> = {
  [AppleMediaStateEnum.APPROVED]: "Approved",
  [AppleMediaStateEnum.DELETED]: "Deleted",
  [AppleMediaStateEnum.IN_REVIEW]: "In review",
  [AppleMediaStateEnum.REJECTED]: "Rejected",
  [AppleMediaStateEnum.SUBMITTED]: "Submitted",
};

export const BRAND_CONTRACT_STATE_LABELS: Record<string, string> = {
  [DelegatedBrandContractStateEnum.APPROVED]: "Approved",
  [DelegatedBrandContractStateEnum.IN_REVIEW]: "In review",
  [DelegatedBrandContractStateEnum.REJECTED]: "Rejected",
  [DelegatedBrandContractStateEnum.FAILED]: "Failed",
  [DelegatedBrandContractStateEnum.OTHER]: "Other",
};

/**
 * The brand-asset PAIR state. Six values, computed by the BACKEND
 * (`computeBrandAssetPairState`) from both assets together.
 *
 * ⚠️ THIS CONSOLE RENDERS ONE STRING AND CONTAINS NO TRUTH TABLE. The mapping from two Apple states to
 * one word is a decision cascade whose ORDER is the specification, and it lives in the backend where
 * there is a test runner to hold it. Duplicating it here would be a second, untested copy of a rule
 * that has to agree with the first — so this file only names the six outcomes.
 *
 * ⚠️ `IN_REVIEW` DELIBERATELY DOES NOT SAY "1 of 2". A pair with one asset live and one still in
 * review is NOT half-live: Apple renders the branding as a SET, so the place card shows NOTHING until
 * both are published. "1 of 2" would read as partial progress that a client can already see, which is
 * the opposite of the truth. The backend asserts no rendered label contains that string.
 */
export const BRAND_ASSET_PAIR_STATE_LABELS: Record<string, string> = {
  NOT_STARTED: "Not started",
  UPLOADING: "Uploading to Apple",
  IN_REVIEW: "In review — 0 of 2 live",
  ACTION_NEEDED: "Action needed — Apple refused one",
  INCOMPLETE: "Incomplete — place card shows NO branding",
  LIVE: "Live — both published",
};

export const BRAND_ORIGIN_LABELS: Record<string, string> = {
  [DelegatedBrandOriginEnum.CREATED]: "Created by DearDoc",
  [DelegatedBrandOriginEnum.RESOLVED_EXISTING]: "Adopted an existing brand",
};

export const MEDIA_CONTRACT_STATE_LABELS: Record<string, string> = {
  [DelegatedMediaContractStateEnum.PENDING]: "Pending Apple review",
  [DelegatedMediaContractStateEnum.APPROVED]: "Approved",
  [DelegatedMediaContractStateEnum.REJECTED]: "Rejected",
  [DelegatedMediaContractStateEnum.OTHER]: "Other",
};

export const LOCATION_CONTRACT_STATE_LABELS: Record<string, string> = {
  [DelegatedLocationContractStateEnum.SUBMISSION_FAILED]: "Submission failed",
  [DelegatedLocationContractStateEnum.REJECTED]: "Rejected",
  [DelegatedLocationContractStateEnum.ACTION_REQUIRED]: "Action required",
  [DelegatedLocationContractStateEnum.CREATED]: "Created",
  [DelegatedLocationContractStateEnum.PUBLISHED]: "Published",
  [DelegatedLocationContractStateEnum.FAILED]: "Failed",
  [DelegatedLocationContractStateEnum.OTHER]: "Other",
};

export const SHOWCASE_CONTRACT_STATE_LABELS: Record<string, string> = {
  [DelegatedShowcaseRequestContractStateEnum.SUBMISSION_FAILED]: "Submission failed",
  [DelegatedShowcaseRequestContractStateEnum.REJECTED]: "Rejected",
  [DelegatedShowcaseRequestContractStateEnum.PARTIALLY_CREATED]:
    "Partially created — a creative exists",
  [DelegatedShowcaseRequestContractStateEnum.SUBMITTED]: "Submitted",
};

export const SHOWCASE_CTA_LABELS: Record<string, string> = {
  [ShowcaseCreativeCtaEnum.SCHEDULE]: "Schedule (booking)",
  [ShowcaseCreativeCtaEnum.WEBSITE]: "Website",
};

/**
 * Apple's `source` vocabulary for a location asset — who supplied the photo. Transcribed from the
 * backend's `AppleLocationAssetSourceEnum`.
 *
 * `BUSINESS` is the default and the only one that normally applies: these are photos a practice
 * supplies of its own premises. ⚠️ Declared as a `const` object rather than an enum because
 * `OnboardingGalleryPhotoInput.source` is typed as `keyof typeof` it — one list, used both as the
 * form's dropdown and as the type.
 */
export const GALLERY_PHOTO_SOURCE_LABELS = {
  BUSINESS: "The business itself",
  USER: "A user / customer",
  VENDOR: "A vendor or agency",
};

export const BRAND_OWNERSHIP_TYPE_LABELS: Record<string, string> = {
  [BrandOwnershipTypeEnum.BRAND_OWNER]: "Brand owner",
  [BrandOwnershipTypeEnum.FRANCHISEE]: "Franchisee",
};

export const BRAND_URL_TYPE_LABELS: Record<string, string> = {
  [BrandUrlTypeEnum.HOMEPAGE]: "Homepage",
  [BrandUrlTypeEnum.FACEBOOK]: "Facebook",
  [BrandUrlTypeEnum.INSTAGRAM]: "Instagram",
  [BrandUrlTypeEnum.TWITTER]: "Twitter",
  [BrandUrlTypeEnum.YELP]: "Yelp",
  [BrandUrlTypeEnum.IOS_APP]: "iOS app",
};

export const LOCATION_OPENING_DAY_LABELS: Record<string, string> = {
  [LocationOpeningDayEnum.monday]: "Monday",
  [LocationOpeningDayEnum.tuesday]: "Tuesday",
  [LocationOpeningDayEnum.wednesday]: "Wednesday",
  [LocationOpeningDayEnum.thursday]: "Thursday",
  [LocationOpeningDayEnum.friday]: "Friday",
  [LocationOpeningDayEnum.saturday]: "Saturday",
  [LocationOpeningDayEnum.sunday]: "Sunday",
};

export const LOCATION_STATUS_LABELS: Record<string, string> = {
  [LocationStatusEnum.OPEN]: "Open",
  [LocationStatusEnum.CLOSED]: "Closed",
};

/**
 * The payment methods Apple accepts on a location — **19 values**, the
 * `locationDetails.paymentMethods[*]` rows of Apple's spec enum dump
 * `bc-payment-methods-spec-enum-values.csv`. The keys are Apple's own values, sent verbatim; the
 * labels are plain English for staff.
 *
 * ⚠️ A MIRROR OF THE BACKEND'S `PAYMENT_METHOD_VALUES`, and it fails safe the same way
 * `APPLE_LOCATION_ATTRIBUTE_ENUM_VALUES` does: the backend re-checks every value against its own copy
 * and refuses an unknown one with a named reason and NO Apple call. So a stale entry here produces an
 * actionable step failure, never a bad write to a client's place card.
 *
 * ⚠️ THE FULL 19 ARE OFFERED, not a medical shortlist, and that is load-bearing rather than lazy. The
 * backend replaces this array wholesale on an update (a flat scalar list has no per-entry identity to
 * merge on), so anything a client set in Business Connect and this form cannot express would be lost
 * with no way to restore it. Offering everything Apple accepts means the form can always put it back.
 */
export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  AMERICAN_EXPRESS: "American Express",
  BITCOIN: "Bitcoin",
  CASH_PAYMENT: "Cash",
  CHECK: "Check",
  DEBIT_CARDS: "Debit cards",
  DINERS_CLUB: "Diners Club",
  DISCOVER: "Discover",
  FINANCING: "Financing / payment plans",
  INVOICE: "Invoice / bill me",
  JCB: "JCB",
  MAESTRO: "Maestro",
  MASTERCARD: "Mastercard",
  MASTERCARD_DEBIT: "Mastercard debit",
  PAYPAL: "PayPal",
  RUPAY: "RuPay",
  STORE_CARD: "Store card",
  UNION_PAY: "UnionPay",
  VISA: "Visa",
  VISA_DEBIT: "Visa debit",
};
